// frontend/src/contexts/AuthContext.js
import React, { createContext, useState, useEffect } from "react";
import api from "../services/api"; // เพิ่ม import api
import toast from "react-hot-toast"; // เพิ่ม import toast

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  useEffect(() => {
    console.log("🔍 AuthProvider - token:", token);
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      console.log("📡 Fetching user profile...");
      const response = await api.get("/", {
        params: {
          endpoint: "user-profile",
        },
      });

      console.log("📡 User profile response:", response.data);

      if (response.data?.status === "success") {
        setUser(response.data.data);
        console.log("✅ User set:", response.data.data);
      } else {
        logout();
      }
    } catch (error) {
      console.error("Failed to fetch user:", error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post("/", {
        endpoint: "login",
        email: email,
        password: password,
      });

      console.log("📡 Login response:", response.data);

      if (response.data?.status === "success") {
        const { token, user } = response.data;

        // เก็บ token ใน localStorage เสมอ (จำเป็นสำหรับ authentication)
        localStorage.setItem("token", token);

        // ไม่ต้องเก็บ rememberMe ซ้ำ เพราะเราจะใช้ token อย่างเดียว
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        setToken(token);
        setUser(user);

        toast.success("เข้าสู่ระบบสำเร็จ");
        return response.data;
      } else {
        throw new Error(response.data?.message || "เข้าสู่ระบบล้มเหลว");
      }
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || "เข้าสู่ระบบล้มเหลว";
      toast.error(message);
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post("/", {
        endpoint: "register",
        ...userData,
      });

      if (response.data?.status === "success") {
        toast.success("สมัครสมาชิกสำเร็จ กรุณาเข้าสู่ระบบ");
        return response.data;
      } else {
        throw new Error(response.data?.message || "สมัครสมาชิกล้มเหลว");
      }
    } catch (error) {
      const message =
        error.response?.data?.message || error.message || "สมัครสมาชิกล้มเหลว";
      toast.error(message);
      throw error;
    }
  };

  const logout = () => {
    console.log("🚪 Logging out...");
    localStorage.removeItem("token");
    delete api.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
    toast.success("ออกจากระบบสำเร็จ");
  };

  // frontend/src/contexts/AuthContext.js
  const updateProfile = async (profileData) => {
    try {
      console.log("📡 Updating profile with data:", profileData);

      // ส่งเฉพาะฟิลด์ที่มีค่า (ไม่ส่งฟิลด์ที่ว่าง)
      const dataToSend = {};
      Object.keys(profileData).forEach((key) => {
        if (
          profileData[key] !== null &&
          profileData[key] !== undefined &&
          profileData[key] !== ""
        ) {
          dataToSend[key] = profileData[key];
        }
      });

      const response = await api.post("/", {
        endpoint: "update-profile",
        ...dataToSend,
      });

      console.log("📡 Update profile response:", response.data);

      if (response.data?.status === "success") {
        // อัปเดต user ใน state ด้วยข้อมูลใหม่
        setUser((prevUser) => ({
          ...prevUser,
          ...response.data.data,
        }));
        toast.success("อัปเดตโปรไฟล์สำเร็จ");
        return response.data;
      } else {
        throw new Error(response.data?.message || "อัปเดตโปรไฟล์ล้มเหลว");
      }
    } catch (error) {
      console.error("❌ Error in updateProfile:", error);
      const message = error.response?.data?.message || "อัปเดตโปรไฟล์ล้มเหลว";
      toast.error(message);
      throw error;
    }
  };

  const refreshUser = async () => {
    try {
      const response = await api.get("/", {
        params: {
          endpoint: "user-profile",
        },
      });

      if (response.data?.status === "success") {
        setUser(response.data.data);
        return response.data.data;
      }
    } catch (error) {
      console.error("Error refreshing user:", error);
    }
  };

  const isAuthenticated = !!user;
  const isAdmin = user?.role === "super_admin" || user?.role === "staff";
  const isSuperAdmin = user?.role === "super_admin";
  const isStaff = user?.role === "staff";
  const isLearner = user?.role === "learner";

  console.log("🔍 AuthContext state:", { user, isAuthenticated, isAdmin });

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        updateProfile,
        refreshUser, // เพิ่มตรงนี้
        isAuthenticated,
        isAdmin,
        isSuperAdmin,
        isStaff,
        isLearner,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
